/**
 * Agent Sharing Service
 * Handles sharing AI Agents via shareable links (same pattern as space sharing)
 */

import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import type { AgentShare, AgentShareAccess, AgentFolderShare, AgentFolderShareAccess } from "@shared/schema";
import { agentFoldersService } from "@/services/agentFoldersService";

export interface CreateAgentShareLinkParams {
  agentId: string;
  accessType?: "restricted" | "anyone" | "organization";
  expiresAt?: Date | null;
}

export interface SharedAgentInfo {
  id: string;
  name: string;
  description: string;
}

export interface AgentShareLinkValidationResult {
  isValid: boolean;
  share: AgentShare | null;
  agent: SharedAgentInfo | null;
  access: AgentShareAccess | null;
  message: string;
}

export interface CreateAgentFolderShareLinkParams {
  folderId: string;
  accessType?: "restricted" | "anyone" | "organization";
  expiresAt?: Date | null;
}

export interface SharedFolderInfo {
  id: string;
  name: string;
  agentCount: number;
}

export interface SharedFolderAgentPreview {
  id: string;
  name: string;
  description: string;
}

export interface AgentFolderShareLinkValidationResult {
  isValid: boolean;
  share: AgentFolderShare | null;
  folder: SharedFolderInfo | null;
  agents: SharedFolderAgentPreview[];
  access: AgentFolderShareAccess | null;
  message: string;
}

class AgentSharingService {
  private generateShareToken(): string {
    const randomBytes = new Uint8Array(24);
    crypto.getRandomValues(randomBytes);
    const base64 = btoa(String.fromCharCode(...randomBytes));
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "").substring(0, 32);
  }

  private async getCurrentUserId(): Promise<string> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured. Please try again later.");
    }
    const session = await getCurrentSession();
    if (!session?.user) {
      throw new Error("User not authenticated");
    }
    return session.user.id;
  }

  private mapToAgentShare(item: any): AgentShare {
    return {
      id: item.id,
      agentId: item.agent_id,
      createdByUserId: item.created_by_user_id,
      shareToken: item.share_token,
      accessType: item.access_type,
      expiresAt: item.expires_at,
      isActive: item.is_active,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    };
  }

  private mapToAgentShareAccess(item: any): AgentShareAccess {
    return {
      id: item.id,
      shareId: item.share_id,
      userId: item.user_id,
      status: item.status,
      accessedAt: item.accessed_at,
      respondedAt: item.responded_at,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    };
  }

  /**
   * Copy the shared agent into the recipient's account (copy-on-accept).
   * Returns the new agent id, or null on failure.
   */
  private mapToAgentFolderShare(item: Record<string, unknown>): AgentFolderShare {
    return {
      id: item.id as string,
      folderId: (item.folder_id ?? item.folderId) as string,
      createdByUserId: (item.created_by_user_id ?? item.createdByUserId) as string,
      shareToken: (item.share_token ?? item.shareToken) as string,
      accessType: (item.access_type ?? item.accessType) as string,
      expiresAt: (item.expires_at ?? item.expiresAt) as Date | null,
      isActive: (item.is_active ?? item.isActive) as string,
      createdAt: (item.created_at ?? item.createdAt) as Date,
      updatedAt: (item.updated_at ?? item.updatedAt) as Date,
    };
  }

  private mapToAgentFolderShareAccess(item: Record<string, unknown>): AgentFolderShareAccess {
    return {
      id: item.id as string,
      shareId: (item.share_id ?? item.shareId) as string,
      userId: (item.user_id ?? item.userId) as string,
      status: (item.status) as string,
      accessedAt: (item.accessed_at ?? item.accessedAt) as Date | null,
      respondedAt: (item.responded_at ?? item.respondedAt) as Date | null,
      copiedFolderId: (item.copied_folder_id ?? item.copiedFolderId) as string | null,
      createdAt: (item.created_at ?? item.createdAt) as Date,
      updatedAt: (item.updated_at ?? item.updatedAt) as Date,
    };
  }

  private async copyAgentToRecipient(
    agentId: string,
    recipientUserId: string,
    targetFolderId?: string | null
  ): Promise<string | null> {
    const { data: source, error: fetchError } = await supabase
      .from("ai_agents")
      .select("*")
      .eq("id", agentId)
      .single();

    if (fetchError || !source) {
      console.error("Failed to fetch source agent for copy:", fetchError);
      return null;
    }

    const insertPayload: Record<string, unknown> = {
      user_id: recipientUserId,
      name: source.name,
      description: source.description ?? "",
      node_type: source.node_type ?? "specialist",
      category: source.category ?? "general",
      icon: source.icon ?? "Bot",
      color: source.color ?? "purple",
      tags: source.tags ?? [],
      access: source.access ?? "free",
      persona: source.persona ?? {},
      capability_modules: source.capability_modules ?? [],
      memory_policy: source.memory_policy ?? { type: "session", includeContext: true },
    };
    if (source.collaboration_rules != null) insertPayload.collaboration_rules = source.collaboration_rules;
    if (source.preferred_model != null) insertPayload.preferred_model = source.preferred_model;
    if (source.context_window != null) insertPayload.context_window = source.context_window;
    if (source.max_iterations != null) insertPayload.max_iterations = source.max_iterations;
    if (source.is_template != null) insertPayload.is_template = source.is_template;
    if (source.composed_agent_ids != null) insertPayload.composed_agent_ids = source.composed_agent_ids;
    if (source.lens != null) insertPayload.lens = source.lens;
    if (source.expert_focus != null) insertPayload.expert_focus = source.expert_focus;
    if (source.output_requirements != null) insertPayload.output_requirements = source.output_requirements;
    if (source.linked_source_ids != null) insertPayload.linked_source_ids = source.linked_source_ids;
    if (source.training_document_ids != null) insertPayload.training_document_ids = source.training_document_ids;
    if (targetFolderId) insertPayload.folder_id = targetFolderId;

    const { data: newAgent, error: insertError } = await supabase
      .from("ai_agents")
      .insert(insertPayload)
      .select("id")
      .single();

    if (insertError || !newAgent) {
      console.error("Failed to copy agent to recipient:", insertError);
      return null;
    }

    return newAgent.id;
  }

  async createShareLink(params: CreateAgentShareLinkParams): Promise<AgentShare> {
    const userId = await this.getCurrentUserId();

    const { data: agent, error: agentError } = await supabase
      .from("ai_agents")
      .select("id, name")
      .eq("id", params.agentId)
      .eq("user_id", userId)
      .maybeSingle();

    if (agentError || !agent) {
      throw new Error("Agent not found or you don't have permission to share it");
    }

    const { data: existingShare } = await supabase
      .from("agent_shares")
      .select("id, agent_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("agent_id", params.agentId)
      .eq("created_by_user_id", userId)
      .eq("is_active", "true")
      .single();

    if (existingShare) {
      const updateData: any = {
        updated_at: new Date().toISOString(),
      };
      if (params.accessType) updateData.access_type = params.accessType;
      if (params.expiresAt !== undefined) updateData.expires_at = params.expiresAt?.toISOString() || null;

      const { data: updatedShare, error: updateError } = await supabase
        .from("agent_shares")
        .update(updateData)
        .eq("id", existingShare.id)
        .select()
        .single();

      if (updateError) {
        throw new Error(`Failed to update share link: ${updateError.message}`);
      }

      return this.mapToAgentShare(updatedShare);
    }

    const shareToken = this.generateShareToken();
    const shareData: any = {
      agent_id: params.agentId,
      created_by_user_id: userId,
      share_token: shareToken,
      access_type: params.accessType || "restricted",
      expires_at: params.expiresAt?.toISOString() || null,
      is_active: "true",
    };

    const { data: newShare, error: createError } = await supabase
      .from("agent_shares")
      .insert(shareData)
      .select()
      .single();

    if (createError) {
      throw new Error(`Failed to create share link: ${createError.message}`);
    }

    return this.mapToAgentShare(newShare);
  }

  async validateShareLink(token: string): Promise<AgentShareLinkValidationResult> {
    const { data: share, error: shareError } = await supabase
      .from("agent_shares")
      .select("id, agent_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) {
      return {
        isValid: false,
        share: null,
        agent: null,
        access: null,
        message: "Link not valid or has been deactivated",
      };
    }

    if (share.expires_at) {
      const expiresAt = new Date(share.expires_at);
      if (expiresAt < new Date()) {
        return {
          isValid: false,
          share: this.mapToAgentShare(share),
          agent: null,
          access: null,
          message: "This share link has expired",
        };
      }
    }

    const { data: agent, error: agentError } = await supabase
      .from("ai_agents")
      .select("id, name, description")
      .eq("id", share.agent_id)
      .maybeSingle();

    if (agentError || !agent) {
      return {
        isValid: false,
        share: this.mapToAgentShare(share),
        agent: null,
        access: null,
        message: "Agent not found or has been deleted",
      };
    }

    let access: AgentShareAccess | null = null;
    try {
      const userId = await this.getCurrentUserId();
      const { data: accessRecord } = await supabase
        .from("agent_share_access")
        .select("id, share_id, user_id, status, accessed_at, responded_at, created_at, updated_at")
        .eq("share_id", share.id)
        .eq("user_id", userId)
        .single();

      if (accessRecord) {
        access = this.mapToAgentShareAccess(accessRecord);
      }
    } catch {
      // User not authenticated
    }

    return {
      isValid: true,
      share: this.mapToAgentShare(share),
      agent: {
        id: agent.id,
        name: agent.name || "",
        description: agent.description || "",
      },
      access,
      message: "Link is valid",
    };
  }

  private async acceptAgentShareByShareId(
    shareId: string,
    agentId: string,
    userId: string,
    targetFolderId?: string | null
  ): Promise<AgentShareAccess> {
    const { data: existingAccess } = await supabase
      .from("agent_share_access")
      .select("id, share_id, user_id, status, accessed_at, responded_at, created_at, updated_at, copied_agent_id")
      .eq("share_id", shareId)
      .eq("user_id", userId)
      .maybeSingle();

    let accessRecord: {
      id: string;
      share_id: string;
      user_id: string;
      status: string;
      accessed_at: string | null;
      responded_at: string | null;
      created_at: string;
      updated_at: string;
      copied_agent_id?: string | null;
    };

    if (existingAccess) {
      if (existingAccess.status !== "accepted") {
        const { data: updatedAccess, error: updateError } = await supabase
          .from("agent_share_access")
          .update({
            status: "accepted",
            responded_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingAccess.id)
          .select()
          .single();

        if (updateError) {
          throw new Error(`Failed to accept invitation: ${updateError.message}`);
        }
        accessRecord = updatedAccess;
      } else {
        accessRecord = existingAccess;
      }
    } else {
      const { data: newAccess, error: createError } = await supabase
        .from("agent_share_access")
        .insert({
          share_id: shareId,
          user_id: userId,
          status: "accepted",
          accessed_at: new Date().toISOString(),
          responded_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (createError) {
        throw new Error(`Failed to accept invitation: ${createError.message}`);
      }
      accessRecord = newAccess;
    }

    if (!accessRecord.copied_agent_id) {
      const newAgentId = await this.copyAgentToRecipient(agentId, userId, targetFolderId);
      if (newAgentId) {
        await supabase
          .from("agent_share_access")
          .update({
            copied_agent_id: newAgentId,
            updated_at: new Date().toISOString(),
          })
          .eq("id", accessRecord.id);
      }
    }

    return this.mapToAgentShareAccess(accessRecord);
  }

  async acceptShareInvitation(token: string): Promise<AgentShareAccess> {
    const userId = await this.getCurrentUserId();

    const { data: share, error: shareError } = await supabase
      .from("agent_shares")
      .select("id, agent_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) {
      throw new Error("Share link not found or invalid");
    }

    return this.acceptAgentShareByShareId(share.id, share.agent_id, userId);
  }

  async rejectShareInvitation(token: string): Promise<AgentShareAccess> {
    const userId = await this.getCurrentUserId();

    const { data: share, error: shareError } = await supabase
      .from("agent_shares")
      .select("id, agent_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) {
      throw new Error("Share link not found or invalid");
    }

    const { data: existingAccess } = await supabase
      .from("agent_share_access")
      .select("id, share_id, user_id, status, accessed_at, responded_at, created_at, updated_at")
      .eq("share_id", share.id)
      .eq("user_id", userId)
      .single();

    if (existingAccess) {
      const { data: updatedAccess, error: updateError } = await supabase
        .from("agent_share_access")
        .update({
          status: "rejected",
          responded_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", existingAccess.id)
        .select()
        .single();

      if (updateError) {
        throw new Error(`Failed to reject invitation: ${updateError.message}`);
      }
      return this.mapToAgentShareAccess(updatedAccess);
    }

    const accessData: any = {
      share_id: share.id,
      user_id: userId,
      status: "rejected",
      accessed_at: new Date().toISOString(),
      responded_at: new Date().toISOString(),
    };

    const { data: newAccess, error: createError } = await supabase
      .from("agent_share_access")
      .insert(accessData)
      .select()
      .single();

    if (createError) {
      throw new Error(`Failed to reject invitation: ${createError.message}`);
    }

    return this.mapToAgentShareAccess(newAccess);
  }

  async revokeShareLink(agentId: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("agent_shares")
      .update({
        is_active: "false",
        updated_at: new Date().toISOString(),
      })
      .eq("agent_id", agentId)
      .eq("created_by_user_id", userId);

    if (error) {
      throw new Error(`Failed to revoke share link: ${error.message}`);
    }

    return true;
  }

  async getShareLink(agentId: string): Promise<AgentShare | null> {
    const userId = await this.getCurrentUserId();

    const { data: share, error } = await supabase
      .from("agent_shares")
      .select("id, agent_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("agent_id", agentId)
      .eq("created_by_user_id", userId)
      .eq("is_active", "true")
      .single();

    if (error || !share) {
      return null;
    }

    return this.mapToAgentShare(share);
  }

  private async ensureAgentShareLinksForFolder(
    folderId: string,
    userId: string,
    accessType: "restricted" | "anyone" | "organization",
    expiresAt?: Date | null
  ): Promise<void> {
    const agents = await agentFoldersService.getAgentsInFolder(folderId);
    if (agents.length === 0) {
      throw new Error("Cannot share an empty folder. Add at least one agent first.");
    }

    for (const agent of agents) {
      await this.createShareLink({
        agentId: agent.id,
        accessType,
        expiresAt,
      });
    }
  }

  async createFolderShareLink(params: CreateAgentFolderShareLinkParams): Promise<AgentFolderShare> {
    const userId = await this.getCurrentUserId();

    const folder = await agentFoldersService.getFolder(params.folderId);
    if (!folder || folder.userId !== userId) {
      throw new Error("Folder not found or you don't have permission to share it");
    }

    const agents = await agentFoldersService.getAgentsInFolder(params.folderId);
    if (agents.length === 0) {
      throw new Error("Cannot share an empty folder. Add at least one agent first.");
    }

    const accessType = params.accessType || "restricted";
    await this.ensureAgentShareLinksForFolder(params.folderId, userId, accessType, params.expiresAt);

    const { data: existingShare } = await supabase
      .from("agent_folder_shares")
      .select("*")
      .eq("folder_id", params.folderId)
      .eq("created_by_user_id", userId)
      .eq("is_active", "true")
      .maybeSingle();

    if (existingShare) {
      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
        access_type: accessType,
      };
      if (params.expiresAt !== undefined) {
        updateData.expires_at = params.expiresAt?.toISOString() || null;
      }

      const { data: updatedShare, error: updateError } = await supabase
        .from("agent_folder_shares")
        .update(updateData)
        .eq("id", existingShare.id)
        .select()
        .single();

      if (updateError) {
        throw new Error(`Failed to update folder share link: ${updateError.message}`);
      }

      return this.mapToAgentFolderShare(updatedShare);
    }

    const shareToken = this.generateShareToken();
    const { data: newShare, error: createError } = await supabase
      .from("agent_folder_shares")
      .insert({
        folder_id: params.folderId,
        created_by_user_id: userId,
        share_token: shareToken,
        access_type: accessType,
        expires_at: params.expiresAt?.toISOString() || null,
        is_active: "true",
      })
      .select()
      .single();

    if (createError) {
      throw new Error(`Failed to create folder share link: ${createError.message}`);
    }

    return this.mapToAgentFolderShare(newShare);
  }

  async validateFolderShareLink(token: string): Promise<AgentFolderShareLinkValidationResult> {
    const { data: share, error: shareError } = await supabase
      .from("agent_folder_shares")
      .select("*")
      .eq("share_token", token)
      .eq("is_active", "true")
      .maybeSingle();

    if (shareError || !share) {
      return {
        isValid: false,
        share: null,
        folder: null,
        agents: [],
        access: null,
        message: "Link not valid or has been deactivated",
      };
    }

    if (share.expires_at) {
      const expiresAt = new Date(share.expires_at);
      if (expiresAt < new Date()) {
        return {
          isValid: false,
          share: this.mapToAgentFolderShare(share),
          folder: null,
          agents: [],
          access: null,
          message: "This share link has expired",
        };
      }
    }

    const { data: folder, error: folderError } = await supabase
      .from("agent_folders")
      .select("id, name")
      .eq("id", share.folder_id)
      .maybeSingle();

    if (folderError || !folder) {
      return {
        isValid: false,
        share: this.mapToAgentFolderShare(share),
        folder: null,
        agents: [],
        access: null,
        message: "Folder not found or has been deleted",
      };
    }

    const { data: agents } = await supabase
      .from("ai_agents")
      .select("id, name, description")
      .eq("folder_id", share.folder_id);

    const agentList = (agents || []).map((a) => ({
      id: a.id,
      name: a.name || "",
      description: a.description || "",
    }));

    if (agentList.length === 0) {
      return {
        isValid: false,
        share: this.mapToAgentFolderShare(share),
        folder: { id: folder.id, name: folder.name, agentCount: 0 },
        agents: [],
        access: null,
        message: "This folder has no agents to share",
      };
    }

    let access: AgentFolderShareAccess | null = null;
    try {
      const userId = await this.getCurrentUserId();
      const { data: accessRecord } = await supabase
        .from("agent_folder_share_access")
        .select("*")
        .eq("share_id", share.id)
        .eq("user_id", userId)
        .maybeSingle();

      if (accessRecord) {
        access = this.mapToAgentFolderShareAccess(accessRecord);
      }
    } catch {
      // not authenticated
    }

    return {
      isValid: true,
      share: this.mapToAgentFolderShare(share),
      folder: {
        id: folder.id,
        name: folder.name,
        agentCount: agentList.length,
      },
      agents: agentList,
      access,
      message: "Link is valid",
    };
  }

  async acceptFolderShareInvitation(token: string): Promise<AgentFolderShareAccess> {
    const userId = await this.getCurrentUserId();

    const validation = await this.validateFolderShareLink(token);
    if (!validation.isValid || !validation.share || !validation.folder) {
      throw new Error(validation.message || "Share link not found or invalid");
    }

    const share = validation.share;
    const folderName = validation.folder.name;

    const { data: existingAccess } = await supabase
      .from("agent_folder_share_access")
      .select("*")
      .eq("share_id", share.id)
      .eq("user_id", userId)
      .maybeSingle();

    let accessRecord: Record<string, unknown>;

    if (existingAccess) {
      if (existingAccess.status !== "accepted") {
        const { data: updatedAccess, error: updateError } = await supabase
          .from("agent_folder_share_access")
          .update({
            status: "accepted",
            responded_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingAccess.id)
          .select()
          .single();

        if (updateError) {
          throw new Error(`Failed to accept folder invitation: ${updateError.message}`);
        }
        accessRecord = updatedAccess;
      } else {
        accessRecord = existingAccess;
      }
    } else {
      const { data: newAccess, error: createError } = await supabase
        .from("agent_folder_share_access")
        .insert({
          share_id: share.id,
          user_id: userId,
          status: "accepted",
          accessed_at: new Date().toISOString(),
          responded_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (createError) {
        throw new Error(`Failed to accept folder invitation: ${createError.message}`);
      }
      accessRecord = newAccess;
    }

    let recipientFolderId = (accessRecord.copied_folder_id as string | null) ?? null;
    if (!recipientFolderId) {
      const recipientFolder = await agentFoldersService.findOrCreateFolderByName(folderName, null);
      recipientFolderId = recipientFolder.id;
      await supabase
        .from("agent_folder_share_access")
        .update({
          copied_folder_id: recipientFolderId,
          updated_at: new Date().toISOString(),
        })
        .eq("id", accessRecord.id);
    }

    for (const agent of validation.agents) {
      const { data: agentShare } = await supabase
        .from("agent_shares")
        .select("id, agent_id")
        .eq("agent_id", agent.id)
        .eq("is_active", "true")
        .maybeSingle();

      if (agentShare) {
        await this.acceptAgentShareByShareId(
          agentShare.id,
          agentShare.agent_id,
          userId,
          recipientFolderId
        );
      } else {
        await this.copyAgentToRecipient(agent.id, userId, recipientFolderId);
      }
    }

    const { data: finalAccess } = await supabase
      .from("agent_folder_share_access")
      .select("*")
      .eq("id", accessRecord.id)
      .single();

    return this.mapToAgentFolderShareAccess(finalAccess || accessRecord);
  }

  async rejectFolderShareInvitation(token: string): Promise<AgentFolderShareAccess> {
    const userId = await this.getCurrentUserId();

    const { data: share, error: shareError } = await supabase
      .from("agent_folder_shares")
      .select("*")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) {
      throw new Error("Share link not found or invalid");
    }

    const { data: existingAccess } = await supabase
      .from("agent_folder_share_access")
      .select("*")
      .eq("share_id", share.id)
      .eq("user_id", userId)
      .maybeSingle();

    if (existingAccess) {
      const { data: updatedAccess, error: updateError } = await supabase
        .from("agent_folder_share_access")
        .update({
          status: "rejected",
          responded_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", existingAccess.id)
        .select()
        .single();

      if (updateError) {
        throw new Error(`Failed to reject invitation: ${updateError.message}`);
      }
      return this.mapToAgentFolderShareAccess(updatedAccess);
    }

    const { data: newAccess, error: createError } = await supabase
      .from("agent_folder_share_access")
      .insert({
        share_id: share.id,
        user_id: userId,
        status: "rejected",
        accessed_at: new Date().toISOString(),
        responded_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (createError) {
      throw new Error(`Failed to reject invitation: ${createError.message}`);
    }

    return this.mapToAgentFolderShareAccess(newAccess);
  }

  async revokeFolderShareLink(folderId: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("agent_folder_shares")
      .update({
        is_active: "false",
        updated_at: new Date().toISOString(),
      })
      .eq("folder_id", folderId)
      .eq("created_by_user_id", userId);

    if (error) {
      throw new Error(`Failed to revoke folder share link: ${error.message}`);
    }

    return true;
  }

  async getFolderShareLink(folderId: string): Promise<AgentFolderShare | null> {
    const userId = await this.getCurrentUserId();

    const { data: share, error } = await supabase
      .from("agent_folder_shares")
      .select("*")
      .eq("folder_id", folderId)
      .eq("created_by_user_id", userId)
      .eq("is_active", "true")
      .maybeSingle();

    if (error || !share) {
      return null;
    }

    return this.mapToAgentFolderShare(share);
  }
}

export const agentSharingService = new AgentSharingService();
